import React, { useState } from "react";
import classNames from "classnames";
import { Amount } from "@design-system/Amount/Amount";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { DetailsAccordionItem } from "@design-system/Accordion/DetailsAccordionItem";
import { ArrowCornerIcon, CloseIcon, CopyIcon, EditIcon } from "@icons";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { detailsAsideCommandBarClassName } from "@design-system/DetailsAside/DetailsAside.styles";
import {
    AccrualMetadataSection,
    AccrualMetadataSectionProps,
} from "./AccrualMetadataSection";
import styles from "./AccrualDetailsPanel.module.scss";

const DEFAULT_OPEN_SECTIONS = ["details"];

interface AccrualDetailsPanelProps {
    /** Rows of the "Details" section — the metadata component's own contract. */
    metadata: AccrualMetadataSectionProps;
    statusBadge?: React.ReactNode;
    amount: number;
    externalUrl?: string;
    externalUrlTitle?: string;
    matchedTransactionsSection?: React.ReactNode;
    /** Where the matches render: their own accordion item, or a row of the Details section. */
    matchesPlacement?: "accordion" | "details";
    activitySection?: React.ReactNode;
    onEdit?: () => void;
    onEditFields?: () => void;
    onView?: () => void;
    viewTooltip?: string;
    onCopy?: () => void;
    additionalActions?: React.ReactNode;
    /** Extra items rendered in the "⋮" management menu, above the delete item. */
    additionalMenuItems?: React.ReactNode;
    onDelete?: () => Promise<void> | void;
    deleteEntityName?: string;
    deleteDisabled?: boolean;
    deleteDisabledTooltip?: string;
    onClose: () => void;
    hideCloseButton?: boolean;
    showLeftBorder?: boolean;
    className?: string;
}

export const AccrualDetailsPanel: React.FC<AccrualDetailsPanelProps> = ({
    metadata,
    statusBadge,
    amount,
    externalUrl,
    externalUrlTitle,
    matchedTransactionsSection,
    matchesPlacement = "accordion",
    activitySection,
    onEdit,
    onEditFields,
    onView,
    viewTooltip = "View invoice",
    onCopy,
    additionalActions,
    additionalMenuItems,
    onDelete,
    deleteEntityName,
    deleteDisabled = false,
    deleteDisabledTooltip,
    onClose,
    hideCloseButton = false,
    showLeftBorder = false,
    className,
}) => {
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const externalProviderLabel = externalUrlTitle?.replace(/^View in /, "");
    const shouldShowMatchesSection = Boolean(matchedTransactionsSection);
    const isMatchesInDetails = matchesPlacement === "details";

    return (
        <div
            className={classNames(styles.panel, className, {
                [styles.leftBorder]: showLeftBorder,
            })}
        >
            <section className={styles.headerSection}>
                <div className={styles.header}>
                    {statusBadge ?? <span />}
                    {!hideCloseButton && (
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={onClose}
                            iconOnly
                            aria-label="Close details panel"
                        >
                            <CloseIcon />
                        </Button>
                    )}
                </div>

                {metadata.counterpartyName && (
                    <span className={styles.counterpartyName}>
                        {metadata.counterpartyName}
                    </span>
                )}

                <Amount
                    className={styles.amount}
                    amount={amount}
                    separateCurrencySign
                    showNegative={false}
                />
            </section>

            <div className={styles.content}>
                <AccordionRoot defaultValue={DEFAULT_OPEN_SECTIONS}>
                    <DetailsAccordionItem value="details" label="Details">
                        <AccrualMetadataSection
                            {...metadata}
                            matchesSection={
                                isMatchesInDetails && shouldShowMatchesSection
                                    ? matchedTransactionsSection
                                    : undefined
                            }
                        />
                    </DetailsAccordionItem>

                    {shouldShowMatchesSection && !isMatchesInDetails && (
                        <DetailsAccordionItem value="matches" label="Matches">
                            {matchedTransactionsSection}
                        </DetailsAccordionItem>
                    )}

                    {activitySection && (
                        <DetailsAccordionItem value="activity" label="Activity">
                            {activitySection}
                        </DetailsAccordionItem>
                    )}
                </AccordionRoot>

                <div className={styles.contentSpacer} aria-hidden />
            </div>

            <CommandIsland className={detailsAsideCommandBarClassName}>
                <div className={styles.toolbarActions}>
                    {externalUrl && externalProviderLabel && (
                        <Tooltip
                            key={`external-${externalUrlTitle}`}
                            content={externalUrlTitle}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                    window.open(
                                        externalUrl,
                                        "_blank",
                                        "noopener,noreferrer",
                                    )
                                }
                                iconOnly
                                aria-label={
                                    externalUrlTitle ?? "Open external invoice"
                                }
                            >
                                <ArrowCornerIcon />
                            </Button>
                        </Tooltip>
                    )}
                    {onEditFields && (
                        <Tooltip content="Edit invoice">
                            <Button
                                variant="secondary"
                                size="md"
                                iconOnly
                                aria-label="Edit invoice"
                                onClick={onEditFields}
                            >
                                <EditIcon />
                            </Button>
                        </Tooltip>
                    )}
                    {onEdit && (
                        <Button
                            variant="secondary"
                            size="md"
                            iconOnly
                            onClick={onEdit}
                            aria-label="Edit"
                        >
                            <EditIcon />
                        </Button>
                    )}
                    {onCopy && (
                        <Button
                            variant="secondary"
                            size="md"
                            iconOnly
                            onClick={onCopy}
                            aria-label="Copy"
                        >
                            <CopyIcon />
                        </Button>
                    )}
                    {additionalActions}
                </div>
                <div className={styles.toolbarActions}>
                    {onView && (
                        <Tooltip
                            key={`view-${viewTooltip}`}
                            content={viewTooltip}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onView}
                                rightIcon={<ArrowCornerIcon />}
                            >
                                View
                            </Button>
                        </Tooltip>
                    )}
                    {(!!onDelete || !!additionalMenuItems) && (
                        <Menu.More>
                            {additionalMenuItems}
                            {onDelete && (
                                <Menu.Item
                                    destructive
                                    disabled={deleteDisabled}
                                    disabledReason={deleteDisabledTooltip}
                                    onClick={() =>
                                        setShowDeleteConfirmation(true)
                                    }
                                >
                                    {`Delete ${deleteEntityName ?? "item"}`}
                                </Menu.Item>
                            )}
                        </Menu.More>
                    )}
                </div>
            </CommandIsland>

            {onDelete && (
                <ConfirmationModal
                    show={showDeleteConfirmation}
                    onHide={() => setShowDeleteConfirmation(false)}
                    title={`Delete ${deleteEntityName ?? "item"}`}
                    question={`Are you sure you want to delete this ${deleteEntityName ?? "item"}?`}
                    yes="Yes"
                    yesBtnVariant="destructive"
                    no="Cancel"
                    onConfirm={async () => {
                        try {
                            await onDelete();
                        } finally {
                            setShowDeleteConfirmation(false);
                        }
                    }}
                    onReject={() => setShowDeleteConfirmation(false)}
                />
            )}
        </div>
    );
};
